import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import TopPanel from './top-panel'
import Games from './games'
import UpcomingGames from './upcoming-games'
import GameModal from '../../containers/open-game-modal'
import GameForm from '../open-game-form'
import SelectButtons from './select-buttons'
import ConfirmationModal from './confirmation-modal'
import CancellationModal from '../cancel-open-game-modal'
import NewMessages from './new-messages'
import Cable from 'actioncable'
import FlashAlert from '../flash-alert'

function OpenGames(props) {
  const [showModal, setShowModal] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const filter = props.filter
  const sort = filter.sort

  useEffect(() => {
    const cable = Cable.createConsumer(props.socketUrl)
    const gameChannel = cable.subscriptions.create({ channel: 'OpenGameChannel' },
    {
      received: (data) => { props.onGameUpdate(data.message) }
    })
    const gamePaymentChannel = cable.subscriptions.create({ channel: 'OpenGamePaymentsChannel' }, 
    {
      received: (data) => { props.onGamePaymentStatus(data.message) }
    })
    const locationsChannel = cable.subscriptions.create({ channel: 'LocationsChannel' }, 
    {
      received: (data) => { if (data.open_game) props.onLocationsUpdate(data.open_game) }
    })
    props.subscribeAdminMessages()
    return () => {
      cable.subscriptions.remove(gameChannel)
      cable.subscriptions.remove(gamePaymentChannel)
      cable.subscriptions.remove(locationsChannel)
      props.unsubscribeAdminMessages()
    }
  }, [])

  const duplicateGame = (id) => {
    setShowModal(false)
    setShowForm({type: 'duplicate', id: id})
  }

  const updateGame = (game) => {
    setShowForm(false)
    props.onRefreshGame(game)
  }

  const timeInMinutes = (time) => {
    return time.hour()*60 + time.minutes()
  }

  const filteredEvents = (events) => {
    if (filter.isDisabled()) return events

    return _.reject(events, function(event) {
      const locationTime = moment().tz(event.timezone)
      const gameStartTime = moment(event.time)
      const gameEndTime = moment(event.time).add(event.duration, 'hours')

      const current = timeInMinutes(locationTime)
      const isGameStarted = timeInMinutes(gameStartTime) <= current
      const isGameEnded = timeInMinutes(gameEndTime) < current
      const isGameFull = event.number_of_players + event.number_of_fakes == event.total_spots

      const hideEnded = filter.hideEnded && isGameStarted && isGameEnded
      const hideHappening = filter.hideHappening && isGameStarted && !isGameEnded
      const hideSeen = filter.hideSeen && !props.unseen.includes(event.channel_id)
      const hideConfirmed = filter.hideConfirmed && event.confirmed
      const hideNotDeclined = filter.hideNotDeclined && !event.declined.length
      const hideToDoComplete = filter.hideToDoComplete && (event.todos_count == event.todos_done_count)
      const hideConfirmedWithFacility = filter.confirmedWithFacility && event.confirmed_with_facility
      const requiresCaptain = filter.requiresCaptain && !event.requires_captain
      const hasCaptain = filter.hasCaptain && !event.has_captain
      const missingCaptain = filter.missingCaptain && event.has_captain
      const lowRating = filter.lowRating && !event.low_rating
      const fakesOff = filter.fakesOff && event.include_fakes
      const newFacility = filter.newFacilities && props.new_locations[event.location_id] == undefined
      const fullWithFakes = filter.fullWithFakes && (event.number_of_fakes == 0 || !isGameFull)
      const hideOrganizerGames = filter.hideOrganizerGames && event.organizer_game

      return hideEnded || hideHappening || hideSeen || hideConfirmed || hideNotDeclined || hideToDoComplete || 
        fakesOff || newFacility || hideConfirmedWithFacility || requiresCaptain || hasCaptain || 
        missingCaptain || fullWithFakes || lowRating || hideOrganizerGames
    })
  }

  const sortedEvents = (events) => {
    return _.orderBy(events, Object.keys(sort), Object.values(sort))
  }

  const onBatchCancel = () => {
    props.onEndSelector()
    props.setAlert({ text: 'Games cancellation scheduled', type: 'alert' })
  }

  const resultEvents = sortedEvents(filteredEvents(props.events))

  const onUpcoming = () => { props.onUpcoming(!props.upcoming) }

  return <section className='wrapper reservation-calendar'>
    { props.alert && <FlashAlert text={props.alert.text} type={props.alert.type} onHide={props.clearAlert}/>}
    <div className='row'>
      <div className='col-lg-12'>
        <section className='reservation-calendar__panel'>
          { showModal &&
            <GameModal
              activeTab={showModal.activeTab}
              gameId={showModal.gameId}
              socketUrl={props.socketUrl}
              onClose={() => setShowModal(false)}
              onDuplicate={duplicateGame}
              onEdit={(data) => setShowForm(data) }
              onCancel={() => setShowModal(false)}
            />
          }
          { showForm && 
            <GameForm 
              type={showForm.type} 
              id={showForm.id} 
              recurringId={showForm.recurringId} 
              onClose={() => setShowForm(false)}
              onUpdate={updateGame}
            /> 
          }
          <TopPanel 
            summary={props.summary} 
            date={props.date}
            archiveStart={props.archive_start}
            showUpcoming={props.upcoming}
            onUpcoming={onUpcoming}
            cancelled={props.cancelled}
            filter={props.filter}
            onDateChange={props.onDateChange} 
            onQuery={props.onQuery}
            onCancelled={props.onCancelled}
            onNewGame={(data) => setShowForm(data)}
            setFilter={props.setFilter}
            onGamesSelect={props.onGamesSelect}
            gamesSelector={props.gamesSelector}
          />
          { props.upcoming
            ? <UpcomingGames
                upcomingEvents={resultEvents}
                newLocations={props.new_locations}
                unseen={props.unseen}
                onTodoChange={props.onTodoChange} 
                onShowGameModal={(data) => setShowModal(data)}
                onEditGame={(data) => setShowForm(data)}
                onUpdateWithFacility={props.onUpdateWithFacility}
                gamesSelector={props.gamesSelector}
                onGamesSelect={props.onGamesSelect}
                isLoading={props.isLoading}
              />
            : <Games 
                events={resultEvents}
                unseen={props.unseen}
                newLocations={props.new_locations}
                onTodoChange={props.onTodoChange} 
                onShowGameModal={(data) => setShowModal(data)}
                onEditGame={(data) => setShowForm(data)}
                onUpdateWithFacility={props.onUpdateWithFacility}
                gamesSelector={props.gamesSelector}
                onGamesSelect={props.onGamesSelect}
                isLoading={props.isLoading}
              />
          }
        </section>
      </div>
      { !!props.gamesSelector.selected.length &&
        <SelectButtons
          gamesSelector={props.gamesSelector}
          onGamesSelect={props.onGamesSelect}
        />
      }
      { props.gamesSelector.action && props.gamesSelector.action == 'confirm' &&
        <ConfirmationModal
          onConfirm={props.onBatchConfirm}
          onCancel={() => props.onGamesSelect({...props.gamesSelector, action: null})}
        />
      }
      { props.gamesSelector.action && props.gamesSelector.action == 'cancel' &&
        <CancellationModal
          gameIds={props.gamesSelector.selected}
          onClose={() => props.onGamesSelect({...props.gamesSelector, action: null})}
          onCancel={onBatchCancel}
        />
      }
      <NewMessages
        events={props.gameNotifications.slice(0, 6)}
        onClose={props.onCloseNotification}
        onShowGameModal={(data) => setShowModal(data)}
      />
    </div>
  </section>
}

OpenGames.propTypes = {
  events: PropTypes.array,
  summary: PropTypes.object,
  new_locations: PropTypes.object,
  date: PropTypes.string,
  archive_start: PropTypes.string,
  query: PropTypes.string,
  cancelled: PropTypes.bool,
  socketUrl: PropTypes.string,
  alert: PropTypes.object,
  filter: PropTypes.object,
  unseen: PropTypes.array,
  gameNotifications: PropTypes.array,
  onDateChange: PropTypes.func.isRequired,
  onQuery: PropTypes.func.isRequired,
  onCancelled: PropTypes.func.isRequired,
  onGameUpdate: PropTypes.func.isRequired,
  onLocationsUpdate: PropTypes.func.isRequired,
  onRefreshGame: PropTypes.func.isRequired,
  onBatchConfirm: PropTypes.func.isRequired,
  onEndSelector: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
  clearAlert: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
  subscribeAdminMessages: PropTypes.func.isRequired,
  unsubscribeAdminMessages: PropTypes.func.isRequired,
  onCloseNotification: PropTypes.func.isRequired
}

export default OpenGames
